import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import type { AvailabilityRule, CreateAvailabilityRuleRequest } from '../types';
import { summarizeSchedule } from '../utils/schedule';

import { AvailabilityRuleForm } from './AvailabilityRuleForm';

export interface AvailabilityViewProps {
  rules: AvailabilityRule[];
  isLoading?: boolean;
  hasError?: boolean;
  deletingId?: string | null;
  isSubmitting?: boolean;
  onCreate: (payload: CreateAvailabilityRuleRequest) => void;
  onDelete: (rule: AvailabilityRule) => void;
}

/** Presentational availability-rules settings card: list + add form. */
export function AvailabilityView({
  rules,
  isLoading = false,
  hasError = false,
  deletingId = null,
  isSubmitting = false,
  onCreate,
  onDelete,
}: AvailabilityViewProps) {
  return (
    <Card
      elevation={1}
      sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200' }}
    >
      <CardHeader
        avatar={<EventAvailableOutlinedIcon color="primary" />}
        title="Your availability"
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
        subheader="Recurring open hours and blocks that drive your self-serve booking calendar."
      />
      <CardContent sx={{ pt: 0 }}>
        {hasError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Couldn’t load availability. Try refreshing.
          </Alert>
        )}

        {isLoading ? (
          <Stack sx={{ alignItems: 'center', py: 4 }}>
            <CircularProgress aria-label="Loading availability" />
          </Stack>
        ) : rules.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No availability windows yet. Add your open hours below.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label="Availability windows">
              <caption style={visuallyHidden}>
                Your recurring availability windows
              </caption>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Schedule</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    <Box component="span" sx={visuallyHidden}>
                      Actions
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map(rule => {
                  const id = rule.availability_rule_id ?? '';
                  const isOpen = rule.effect === 'open';
                  return (
                    <TableRow key={id}>
                      <TableCell>
                        <Chip
                          size="small"
                          color={isOpen ? 'success' : 'default'}
                          variant={isOpen ? 'filled' : 'outlined'}
                          label={isOpen ? 'Open' : 'Block'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {summarizeSchedule(rule)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {deletingId === id ? (
                          <CircularProgress size={16} aria-label="Removing" />
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => onDelete(rule)}
                            aria-label={`Remove ${isOpen ? 'open hours' : 'block'} ${summarizeSchedule(rule)}`}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <AvailabilityRuleForm onCreate={onCreate} isSubmitting={isSubmitting} />
      </CardContent>
    </Card>
  );
}
